import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Sweeping Laser — a Terraria/Calamity-style beam that locks onto the
 * player's position during the telegraph, then sweeps across that heading
 * while active. Doesn't use the bullet pool: it's a continuous beam, so
 * hit-detection is a point-to-ray distance check done directly against the
 * player each tick, with its own damage-tick timer (not per-frame) so it
 * doesn't melt the player the instant they're caught in it.
 *
 * `config` (sweepSpeed/tickDamage/tickInterval/width/length) is per-boss
 * tuning, supplied by data/bosses.js.
 */
export class SweepingLaser extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Sweeping Laser', telegraphDuration: 0.6, activeDuration: 1.4, minPhase });
    this.config = config;
  }

  onBegin(boss, player) {
    this._startAngle = Math.atan2(player.y - boss.y, player.x - boss.x);
    this._angle = this._startAngle;
    this._tickTimer = 0;
  }

  onActiveTick(dt, boss, player) {
    const { sweepSpeed, tickDamage, tickInterval, width, length } = this.config;
    this._angle += sweepSpeed * dt;
    this._tickTimer -= dt;
    if (this._tickTimer <= 0 && this._playerInBeam(boss, player, width, length)) {
      player.takeDamage(tickDamage);
      this._tickTimer = tickInterval;
    }
  }

  _playerInBeam(boss, player, width, length) {
    const dx = player.x - boss.x;
    const dy = player.y - boss.y;
    const dist = Math.hypot(dx, dy);
    const pointAngle = Math.atan2(dy, dx);
    let diff = pointAngle - this._angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const alongBeam = Math.cos(diff) * dist;
    const perpFromBeam = Math.abs(Math.sin(diff)) * dist;
    return alongBeam > 0 && alongBeam < length && perpFromBeam < width / 2 + player.radius;
  }

  getVisual(boss) {
    const { width, length } = this.config;
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'laserAim', x: boss.x, y: boss.y, angle: this._startAngle, length };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'laserBeam', x: boss.x, y: boss.y, angle: this._angle, width, length };
    }
    return null;
  }
}
